/**
 * inline_editor_coordination.js — one live inline editor at a time (shared).
 *
 * The detail pages render several independent inline editors (description,
 * price, notes, the Add Price modal), each with its own Save. A user
 * mid-description who taps the price pencil expects BOTH fields to save, so
 * the editors coordinate instead of stacking open forms:
 *
 *   - Before opening, an editor dispatches the cancelable `inline-edit:opening`
 *     CustomEvent on document (detail.source = its root element) via
 *     requestOpen(). dispatchEvent runs listeners synchronously and returns
 *     false iff a peer called preventDefault() — a veto; the caller must not
 *     open.
 *   - Every open peer answers with one of three moves: an untouched form
 *     closes silently; a dirty, silently-savable draft commits fire-and-forget
 *     (typed text is intent — spreadsheet semantics; the save's returned
 *     Turbo Stream swaps the partial back to display mode when it lands); a
 *     draft that CANNOT be silently saved (un-submittable value, or a save
 *     that would detour through a confirm dialog) vetoes the open, keeps
 *     focus, and explains via toast.
 *   - The same commit-or-close runs on turbo:before-cache, so navigating away
 *     mid-edit saves the draft instead of silently discarding it (FormData is
 *     captured synchronously, so the POST survives the body swap). Abandoning
 *     a draft therefore requires an explicit Cancel.
 *   - A live update (a save made on another device or tab, applied by
 *     static/js/sse-entity-updates.js, which marks its streams
 *     data-live-update) never replaces an open, dirty editor: the stream for
 *     that card is held and applied when the editor closes, while every other
 *     card updates at once. The draft is not merged with the change; saving it
 *     meets the server's record version check, which refuses a stale save and
 *     shows both versions (record_conflict_controller.js). Cancel applies the
 *     held update, so a controller's cancel() calls releaseHeldUpdate().
 *
 * Controllers participate declaratively: requestOpen() guards the open, and
 * bindEditorGuards() wires the listeners from five small hooks. The decision
 * tree lives here only — a new inline editor joins by supplying hooks, never
 * by re-implementing the protocol. (The always-visible quick-price form covers
 * the same risk differently, folding the open description draft into its own
 * POST — see price_enquiry_controller.js.)
 */

export const OPENING_EVENT = 'inline-edit:opening';

const LIVE_UPDATE_MARK = 'data-live-update';
const HELD_MESSAGE = 'This was changed on another device. Your unsaved edit is kept.';
const heldUpdates = new WeakMap();   // editor element -> markup of its held live-update stream

/**
 * Apply the live update held back while this editor was open, if any. Called
 * once the editor has closed without saving; a save needs no release, because
 * its own answer replaces the card and supersedes what was held.
 */
export function releaseHeldUpdate(element) {
    const html = heldUpdates.get(element);
    if (!html) return;
    heldUpdates.delete(element);
    window.Turbo.renderStreamMessage(html);
}

/**
 * Announce an editor is about to open so peers can commit/close/veto first.
 * Returns false when a peer vetoed (it kept focus and explained); the caller
 * must not open.
 */
export function requestOpen(sourceElement) {
    return document.dispatchEvent(new CustomEvent(OPENING_EVENT, {
        cancelable: true,
        detail: { source: sourceElement },
    }));
}

/**
 * Wire an inline editor's draft guards: the peer-opening response, the
 * turbo:before-cache commit and the live-update hold. The first two run the
 * same decision tree over the hooks; the hold asks only isOpen and isDirty:
 *
 *   isOpen()    editor form currently visible?
 *   isDirty()   did the user change anything?
 *   canCommit() is a SILENT save possible (submittable, no confirm dialog)?
 *   commit()    fire-and-forget save
 *   close()     swap back to display mode
 *   veto()      keep focus + toast (optional; only reached when a dirty
 *               draft cannot commit — omit when canCommit is always true)
 *
 * Returns an unbind function for disconnect().
 */
export function bindEditorGuards(element, hooks) {
    const onPeerOpening = (event) => {
        if (event.detail && event.detail.source === element) return;
        if (!hooks.isOpen()) return;
        if (!hooks.isDirty()) {
            hooks.close();
            releaseHeldUpdate(element);
            return;
        }
        if (!hooks.canCommit()) {
            event.preventDefault();
            if (hooks.veto) hooks.veto();
            return;
        }
        hooks.commit();
    };
    // A confirm-needing draft cannot ask mid-navigation, so it falls back to
    // the old discard behaviour; everything else commits before the snapshot.
    const onBeforeCache = () => {
        if (hooks.isOpen() && hooks.isDirty() && hooks.canCommit()) hooks.commit();
        hooks.close();
    };
    // A live update aimed at this card (or a card containing it) waits while
    // the user has unsaved text in it. Only the latest is kept: each refresh
    // renders the whole record as it now stands.
    const onStreamRender = (event) => {
        const stream = event.detail && event.detail.newStream;
        if (!stream || !stream.hasAttribute(LIVE_UPDATE_MARK)) return;
        const target = document.getElementById(stream.getAttribute('target'));
        if (!target || !target.contains(element)) return;
        if (!hooks.isOpen() || !hooks.isDirty()) return;
        event.preventDefault();
        if (!heldUpdates.has(element) && typeof window.showNotification === 'function') {
            window.showNotification(HELD_MESSAGE, 'info');
        }
        heldUpdates.set(element, stream.outerHTML);
    };
    document.addEventListener(OPENING_EVENT, onPeerOpening);
    document.addEventListener('turbo:before-cache', onBeforeCache);
    document.addEventListener('turbo:before-stream-render', onStreamRender);
    return () => {
        document.removeEventListener(OPENING_EVENT, onPeerOpening);
        document.removeEventListener('turbo:before-cache', onBeforeCache);
        document.removeEventListener('turbo:before-stream-render', onStreamRender);
        heldUpdates.delete(element);
    };
}
